/**
 * Bottom navigation bar for mobile — replaces the sidebar on small screens.
 *
 * Shows 5 key items based on role:
 * - Sales reps: Queue, Leads, Calls, Tasks, More
 * - Admins/Managers: Dashboard, Queue, Leads, Calls, More
 *
 * "More" opens a full-screen menu with all navigation items.
 * Hidden on md+ screens (desktop shows the sidebar instead).
 */

import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Target, Phone, CheckSquare, ListOrdered,
  Menu, X, Building2, Mail, Users, Settings, BarChart3,
  FileText, Clock, Bell, PhoneMissed, Facebook, Wifi,
  UserCheck, FileArchive, Activity, TrendingUp, Instagram,
  Share2, Megaphone, Lightbulb, Image, Globe, Search,
} from 'lucide-react'
import { useAuthStore } from '../../stores/auth'

interface BottomNavItem {
  to: string
  icon: any
  label: string
}

// Full menu items for the "More" sheet (role-filtered at render time)
const ALL_ITEMS: { section: string; items: { to: string; icon: any; label: string; roles: string[] }[] }[] = [
  {
    section: 'Sales',
    items: [
      { to: '/queue', icon: ListOrdered, label: 'Queue', roles: ['admin', 'manager', 'sales_rep'] },
      { to: '/leads', icon: Target, label: 'Leads', roles: ['admin', 'manager', 'sales_rep'] },
      { to: '/companies', icon: Building2, label: 'Companies', roles: ['admin', 'manager', 'sales_rep'] },
      { to: '/tasks', icon: CheckSquare, label: 'Tasks', roles: ['admin', 'manager', 'sales_rep', 'marketing'] },
      { to: '/email', icon: Mail, label: 'Email', roles: ['admin', 'manager', 'sales_rep'] },
      { to: '/voip', icon: Phone, label: 'Calls', roles: ['admin', 'manager', 'sales_rep'] },
      { to: '/unknown-callers', icon: PhoneMissed, label: 'Unknown Callers', roles: ['admin', 'manager'] },
    ],
  },
  {
    section: 'Tools',
    items: [
      { to: '/tools/blog', icon: FileText, label: 'Blog Writer', roles: ['admin', 'manager', 'marketing'] },
      { to: '/tools/social', icon: Share2, label: 'Social Media', roles: ['admin', 'manager', 'marketing'] },
      { to: '/tools/campaign', icon: Megaphone, label: 'Campaign', roles: ['admin', 'manager', 'marketing'] },
      { to: '/tools/strategy', icon: Lightbulb, label: 'Strategy', roles: ['admin', 'manager', 'marketing'] },
      { to: '/tools/image', icon: Image, label: 'Image Gen', roles: ['admin', 'manager', 'marketing'] },
      { to: '/tools/scraper', icon: Globe, label: 'Scraper', roles: ['admin', 'manager', 'marketing'] },
    ],
  },
  {
    section: 'Insights',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Home', roles: ['admin', 'manager', 'sales_rep', 'marketing'] },
      { to: '/reports', icon: BarChart3, label: 'Reports', roles: ['admin', 'manager'] },
      { to: '/history', icon: Clock, label: 'Run History', roles: ['admin', 'manager', 'marketing'] },
      { to: '/meta', icon: Facebook, label: 'Meta Ads', roles: ['admin', 'manager', 'marketing'] },
      { to: '/meta/analytics', icon: TrendingUp, label: 'Ad Analytics', roles: ['admin', 'manager'] },
      { to: '/social-analytics', icon: Instagram, label: 'Social Analytics', roles: ['admin', 'manager', 'marketing'] },
      { to: '/ig-discovery', icon: Search, label: 'IG Discovery', roles: ['admin', 'manager', 'marketing', 'sales_rep'] },
    ],
  },
  {
    section: 'Management',
    items: [
      { to: '/team', icon: Users, label: 'Teams', roles: ['admin', 'manager'] },
      { to: '/team/workload', icon: UserCheck, label: 'Workload', roles: ['admin', 'manager'] },
      { to: '/team/analytics', icon: BarChart3, label: 'Team Analytics', roles: ['admin', 'manager'] },
      { to: '/presence', icon: Wifi, label: 'Team Activity', roles: ['admin', 'manager'] },
    ],
  },
  {
    section: 'Admin',
    items: [
      { to: '/users', icon: UserCheck, label: 'Users', roles: ['admin'] },
      { to: '/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
      { to: '/data-transfers', icon: FileArchive, label: 'Data Transfers', roles: ['admin', 'manager'] },
      { to: '/tasks-monitor', icon: Activity, label: 'Task Monitor', roles: ['admin'] },
      { to: '/notifications', icon: Bell, label: 'Notifications', roles: ['admin', 'manager', 'marketing', 'sales_rep'] },
    ],
  },
]

export default function BottomNav() {
  const [moreOpen, setMoreOpen] = useState(false)
  const user = useAuthStore((s) => s.user)
  const role = user?.role || 'sales_rep'
  const location = useLocation()

  // Primary tabs based on role
  const primaryTabs: BottomNavItem[] = role === 'sales_rep'
    ? [
        { to: '/queue', icon: ListOrdered, label: 'Queue' },
        { to: '/leads', icon: Target, label: 'Leads' },
        { to: '/voip', icon: Phone, label: 'Calls' },
        { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
      ]
    : role === 'marketing'
    ? [
        { to: '/tools/blog', icon: FileText, label: 'Tools' },
        { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
        { to: '/history', icon: Clock, label: 'History' },
        { to: '/notifications', icon: Bell, label: 'Alerts' },
      ]
    : [
        { to: '/', icon: LayoutDashboard, label: 'Home' },
        { to: '/queue', icon: ListOrdered, label: 'Queue' },
        { to: '/leads', icon: Target, label: 'Leads' },
        { to: '/voip', icon: Phone, label: 'Calls' },
      ]

  return (
    <>
      {/* Bottom bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 md:hidden safe-area-pb">
        <div className="flex items-center justify-around h-14">
          {primaryTabs.map((tab) => {
            const isActive = tab.to === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(tab.to)
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={`flex flex-col items-center justify-center flex-1 h-full text-[10px] font-medium transition-colors ${
                  isActive ? 'text-indigo-600' : 'text-gray-400'
                }`}
                onClick={() => setMoreOpen(false)}
              >
                <tab.icon size={20} strokeWidth={isActive ? 2.5 : 1.5} />
                <span className="mt-0.5">{tab.label}</span>
              </NavLink>
            )
          })}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex flex-col items-center justify-center flex-1 h-full text-[10px] font-medium transition-colors ${
              moreOpen ? 'text-indigo-600' : 'text-gray-400'
            }`}
          >
            {moreOpen ? <X size={20} strokeWidth={2.5} /> : <Menu size={20} strokeWidth={1.5} />}
            <span className="mt-0.5">More</span>
          </button>
        </div>
      </nav>

      {/* More menu — full screen overlay */}
      {moreOpen && (
        <div className="fixed inset-0 z-30 bg-white md:hidden overflow-y-auto pt-14 pb-20">
          <div className="p-4 space-y-4">
            {ALL_ITEMS.map((section) => {
              const filtered = section.items.filter((item) => item.roles.includes(role))
              if (filtered.length === 0) return null
              return (
                <div key={section.section}>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">{section.section}</p>
                  <div className="grid grid-cols-4 gap-2">
                    {filtered.map((item) => {
                      const isActive = item.to === '/'
                        ? location.pathname === '/'
                        : location.pathname.startsWith(item.to)
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setMoreOpen(false)}
                          className={`flex flex-col items-center justify-center p-3 rounded-xl text-[11px] font-medium transition-colors ${
                            isActive
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <item.icon size={20} className="mb-1" />
                          {item.label}
                        </NavLink>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
